import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  listMyInvestments, countersignInvestment, contractUrl,
  type MyInvestment,
} from '../../lib/closing';
import { ApiError } from '../../lib/apiClient';
import { SignaturePad } from '../../components/SignaturePad';
import { formatMoney, formatDateShort } from '../../lib/format';
import './InvestorPages.css';

/**
 * The investor's own investments, and where they sign.
 *
 * THIS IS THE ONLY PLACE A CONTRACT CAN BE SIGNED. Voleto records the deal after it is
 * agreed off platform, but the signature has to be made by the investor, in their own
 * authenticated session, or the contract is one the issuer executed on their behalf and
 * would carry little weight in a dispute. There is deliberately no way for an admin to
 * sign here, and the engine refuses it even if the request is made directly.
 *
 * Signing does not move money and does not activate the holding. The wire happens off
 * platform and Voleto confirms it separately, which is why a signed investment sits at
 * "awaiting funds" rather than looking finished.
 *
 * The live position, ticket sales and projected value are 41e. This screen deliberately
 * shows what is on the contract and nothing more, because a projected value next to an
 * unsigned holding would be quoting a return on something nobody has agreed to yet.
 */

const STATUS: Record<string, { label: string; hint: string; tone: string }> = {
  pending_signature: {
    label: 'Your signature needed',
    hint: 'Voleto has recorded the terms you agreed. Sign below to execute the contract.',
    tone: 'bkr-badge--pending',
  },
  pending_payment: {
    label: 'Signed, awaiting funds',
    hint: 'Your contract is executed. Voleto will confirm your holding once the transfer arrives.',
    tone: 'bkr-badge--pending',
  },
  active: {
    label: 'Active',
    hint: 'Your funds are confirmed and this holding is live.',
    tone: 'bkr-badge--ok',
  },
  cancelled: {
    label: 'Cancelled',
    hint: 'This closing was cancelled and the shares returned to the raise.',
    tone: 'bkr-badge--muted',
  },
  paid_out: {
    label: 'Paid out',
    hint: 'This event has settled and your distribution has been made.',
    tone: 'bkr-badge--ok',
  },
  refunded: { label: 'Refunded', hint: 'This investment was refunded.', tone: 'bkr-badge--muted' },
};

function InvestmentCard({ inv, onSigned }: { inv: MyInvestment; onSigned: () => void }) {
  const [signature, setSignature] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const meta = STATUS[inv.status] ?? { label: inv.status, hint: '', tone: 'bkr-badge--muted' };

  async function sign() {
    if (!signature) return;
    setBusy(true);
    setError(null);
    try {
      await countersignInvestment(inv.id, signature);
      onSigned();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record your signature.');
      setBusy(false);
    }
  }

  return (
    <article className="opp-card glass-card">
      <header className="opp-card__head">
        <div>
          <h3 className="opp-card__title">{inv.eventTitle}</h3>
          <p className="bkr-hint mono">{inv.contractNumber}</p>
        </div>
        <span className={`bkr-badge ${meta.tone}`}>{meta.label}</span>
      </header>

      <dl className="opp-stats">
        <div><dt>Shares</dt><dd className="mono">{inv.shares}</dd></div>
        <div><dt>Invested</dt><dd className="mono">{formatMoney(inv.amountInvestedCents)}</dd></div>
        {/* Share price and entry fee are DIFFERENT figures and are shown as two lines.
            They can coincide (2.5% of 40 units at $500 is also $500), which is exactly why
            collapsing them into one label would be wrong: the price is per unit, the fee
            is 2.5% of the whole. */}
        <div>
          <dt>Share price</dt>
          <dd className="mono">{formatMoney(inv.pricePerShareCents)}</dd>
        </div>
        {/* Historical only. The 2.5% subscription fee was removed, so new holdings carry
            zero and the row is omitted rather than showing a $0.00 line that invites the
            question of what it is. Older contracts genuinely charged one and still show it. */}
        {inv.platformEntryFeeCents > 0 && (
          <div>
            <dt>Entry fee</dt>
            <dd className="mono">{formatMoney(inv.platformEntryFeeCents)}</dd>
          </div>
        )}
        <div><dt>Event date</dt><dd className="mono">{formatDateShort(inv.eventDate)}</dd></div>
      </dl>

      {meta.hint && <p className="bkr-hint">{meta.hint}</p>}

      {inv.status === 'pending_signature' && (
        <div className="bkr-sign">
          <p className="bkr-hint">
            By signing you execute this agreement on the terms above. No payment is taken
            here; Voleto will confirm your transfer separately.
          </p>
          <SignaturePad onChange={setSignature} disabled={busy} label="Sign here" />
          {error && <p className="adm-error" role="alert">{error}</p>}
          <button type="button" className="button button--primary"
            disabled={!signature || busy} onClick={() => void sign()}>
            {busy ? 'Recording your signature…' : 'Sign and execute'}
          </button>
        </div>
      )}

      {inv.status !== 'pending_signature' && inv.status !== 'cancelled' && (
        <footer className="opp-card__foot">
          {/* The ledger is where the live position lives; it reports plainly when a
              holding is not yet funded, so it is worth offering before then too. */}
          <Link className="button button--primary" to={`/investor/investments/${inv.id}`}>
            {inv.status === 'active' ? 'View live ledger' : 'View ledger'}
          </Link>
          <a className="button button--ghost" href={contractUrl(inv.id)}>
            Download contract
          </a>
        </footer>
      )}
    </article>
  );
}

export function InvestorInvestments() {
  const [rows, setRows] = useState<MyInvestment[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    listMyInvestments()
      .then((r) => { setRows(r); setError(null); })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load your investments.'));
  }, []);

  useEffect(load, [load]);

  const needsSignature = (rows ?? []).filter((r) => r.status === 'pending_signature');

  return (
    <div className="bkr-page">
      <header className="bkr-head">
        <h1 className="bkr-title">Your investments</h1>
        <p className="bkr-hint">
          Holdings Voleto has recorded for you, and any contract waiting on your signature.
        </p>
      </header>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {rows == null && <p className="bkr-hint">Loading…</p>}

      {rows != null && rows.length === 0 && (
        <div className="bkr-card">
          <h2 className="bkr-card__title">Nothing here yet</h2>
          <p className="bkr-hint">
            Investments are arranged directly with Voleto and appear here once recorded.
          </p>
        </div>
      )}

      {needsSignature.length > 0 && (
        <p className="bkr-hint">
          <strong>{needsSignature.length}</strong>
          {needsSignature.length === 1 ? ' contract is' : ' contracts are'} waiting on your
          signature.
        </p>
      )}

      <div className="bkr-grid">
        {(rows ?? []).map((inv) => (
          <InvestmentCard key={inv.id} inv={inv} onSigned={load} />
        ))}
      </div>
    </div>
  );
}
